import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import {
  Badge,
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { find, isEmpty, map, size, times } from 'es-toolkit/compat';
import { AgentSession } from 'interfaces/agentSession';
import moment from 'moment';
import React from 'react';

import { CopilotSessionActivityState } from './hooks/useCopilotChatFlow';

interface StudyOption {
  text: string;
  value: string;
}

interface LabOption {
  value: string;
  text: string;
}

interface Props {
  activeSessions: AgentSession[];
  archivedSessions: AgentSession[];
  activeSessionId?: string | null;
  archiveMutationSessionIds?: string[];
  sessionActivityById?: Record<string, CopilotSessionActivityState>;
  isLoading?: boolean;
  isArchivedLoading?: boolean;
  studyOptions?: StudyOption[] | null;
  labs?: LabOption[];
  onSelectSession: (session: AgentSession) => void;
  onArchiveSession: (session: AgentSession) => void;
  onUnarchiveSession: (session: AgentSession) => void;
}

const getStudyNamesFromComponentProps = (
  studyIds?: string[] | null,
  studyOptions?: StudyOption[] | null,
  studyNames?: Record<string, string> | null
) => {
  if (isEmpty(studyIds)) {
    return ['No related studies'];
  }

  return map(studyIds, (studyId) => studyNames?.[studyId] || find(studyOptions, { value: studyId })?.text || studyId);
};

const getSessionLabName = (session: AgentSession, labs?: LabOption[]): string | null => {
  const sessionLabId = session.sessionData?.reportState?.filters?.labId;
  if (!sessionLabId || isEmpty(labs)) return null;
  return find(labs, (lab) => lab.value === sessionLabId)?.text || null;
};

const sessionTitleTypographySx = {
  minWidth: 0,
  display: 'block',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
} as const;

export const CopilotSessionsTab: React.FC<React.PropsWithChildren<Props>> = ({
  activeSessions,
  archivedSessions,
  activeSessionId,
  archiveMutationSessionIds,
  sessionActivityById,
  isLoading,
  isArchivedLoading,
  studyOptions,
  labs,
  onSelectSession,
  onArchiveSession,
  onUnarchiveSession,
}) => {
  const [archivedExpanded, setArchivedExpanded] = React.useState(false);

  if (isLoading) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading saved sessions...
        </Typography>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {times(3, (index) => (
            <Box key={index}>
              <Skeleton variant="text" width={index === 0 ? '18%' : '22%'} height={16} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" width={index === 2 ? '58%' : '72%'} height={18} sx={{ mb: 1 }} />
              <Stack direction="row" spacing={1}>
                <Skeleton variant="rounded" width={92} height={24} />
                <Skeleton variant="rounded" width={index === 1 ? 74 : 88} height={24} />
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  if (isEmpty(activeSessions) && isEmpty(archivedSessions) && isArchivedLoading) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading archived sessions...
        </Typography>
      </Box>
    );
  }

  if (isEmpty(activeSessions) && isEmpty(archivedSessions)) {
    return (
      <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
        <HistoryRoundedIcon color="disabled" sx={{ fontSize: 28, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Saved sessions will appear here after you start a conversation or open a workspace.
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {!isEmpty(activeSessions) && (
        <Typography variant="overline" color="text.secondary" sx={{ px: 2, pt: 1, pb: 0.5, display: 'block' }}>
          Active
        </Typography>
      )}

      {map(activeSessions, (session) => {
        const relatedStudyNames = getStudyNamesFromComponentProps(session.studyIds, studyOptions, session.studyNames);
        const sessionLabName = getSessionLabName(session, labs);
        const updatedAtLabel = session.updatedAt ? moment(session.updatedAt).fromNow() : null;
        const isArchiveMutationPending = archiveMutationSessionIds?.includes(session.id);
        const sessionActivity = sessionActivityById?.[session.id];
        const isLive = Boolean(sessionActivity?.isStreaming);
        const hasUnreadUpdate = Boolean(sessionActivity?.hasUnreadUpdate);

        return (
          <ListItemButton
            key={session.id}
            selected={session.id === activeSessionId}
            alignItems="flex-start"
            onClick={() => onSelectSession(session)}
            sx={{ px: 2, py: 1.5, alignItems: 'stretch' }}
          >
            <ListItemText
              sx={{ minWidth: 0, my: 0 }}
              primary={
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    justifyContent="space-between"
                    sx={{ minWidth: 0 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ pr: 1, flex: 1, minWidth: 0 }}>
                      <Badge
                        color="primary"
                        variant="dot"
                        overlap="circular"
                        invisible={!hasUnreadUpdate}
                        sx={{ display: 'block', flex: 1, minWidth: 0, width: '100%' }}
                      >
                        <Typography variant="subtitle2" sx={sessionTitleTypographySx}>
                          {session.title || 'New analysis session'}
                        </Typography>
                      </Badge>
                      {isLive && <Chip label="Live" size="small" color="secondary" variant="filled" />}
                      {hasUnreadUpdate && <Chip label="New reply" size="small" color="primary" variant="filled" />}
                    </Stack>
                    <Tooltip title="Archive session">
                      <span>
                        <IconButton
                          edge="end"
                          aria-label={`Archive ${session.title || 'session'}`}
                          size="small"
                          disabled={isArchiveMutationPending}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onArchiveSession(session);
                          }}
                        >
                          <ArchiveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {sessionLabName && <Chip label={sessionLabName} size="small" variant="filled" color="default" />}
                    {map(relatedStudyNames, (studyName) => (
                      <Chip key={`${session.id}-${studyName}`} label={studyName} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Stack>
              }
              secondary={
                updatedAtLabel ? (
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
                    <AccessTimeRoundedIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption" color="text.secondary">
                      Updated {updatedAtLabel}
                    </Typography>
                  </Stack>
                ) : null
              }
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItemButton>
        );
      })}

      {isArchivedLoading && isEmpty(archivedSessions) && (
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Loading archived sessions...
          </Typography>
        </Box>
      )}

      {!isEmpty(archivedSessions) && (
        <ListItemButton
          onClick={() => setArchivedExpanded((currentValue) => !currentValue)}
          aria-label={archivedExpanded ? 'Collapse archived sessions' : 'Expand archived sessions'}
          aria-expanded={archivedExpanded}
          sx={{ px: 2, pt: 2, pb: 0.5 }}
        >
          <ListItemText
            primary={
              <Typography variant="overline" color="text.secondary">
                Archived ({size(archivedSessions)})
              </Typography>
            }
          />
          {archivedExpanded ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
        </ListItemButton>
      )}

      <Collapse in={archivedExpanded} timeout="auto" unmountOnExit>
        {map(archivedSessions, (session) => {
          const relatedStudyNames = getStudyNamesFromComponentProps(session.studyIds, studyOptions, session.studyNames);
          const sessionLabName = getSessionLabName(session, labs);
          const archivedAtLabel = session.archivedAt ? moment(session.archivedAt).fromNow() : null;
          const isArchiveMutationPending = archiveMutationSessionIds?.includes(session.id);

          return (
            <Box key={session.id} sx={{ px: 2, py: 1.5, opacity: 0.72 }}>
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  justifyContent="space-between"
                  sx={{ minWidth: 0 }}
                >
                  <Typography variant="subtitle2" sx={{ pr: 1, flex: 1, ...sessionTitleTypographySx }}>
                    {session.title || 'New analysis session'}
                  </Typography>
                  <Tooltip title="Restore session">
                    <span>
                      <IconButton
                        edge="end"
                        aria-label={`Restore ${session.title || 'session'}`}
                        size="small"
                        disabled={isArchiveMutationPending}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onUnarchiveSession(session);
                        }}
                      >
                        <UnarchiveOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {sessionLabName && <Chip label={sessionLabName} size="small" variant="filled" color="default" />}
                  {map(relatedStudyNames, (studyName) => (
                    <Chip key={`${session.id}-${studyName}`} label={studyName} size="small" variant="outlined" />
                  ))}
                </Stack>
                {archivedAtLabel ? (
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <ArchiveOutlinedIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption" color="text.secondary">
                      Archived {archivedAtLabel}
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Collapse>
    </List>
  );
};
